import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const STORAGE_KEY = "sh0p.cart";

const isCurrentCartProduct = (product) => {
  if (!product || typeof product !== "object") return false;

  const hasLegacyProductKeys = [
    "name",
    "price",
    "mrp",
    "stock",
    "variants",
    "photosV2",
    "customisations",
    "headerPhoto",
  ].some((key) => Object.prototype.hasOwnProperty.call(product, key));

  const hasLegacyOptionKeys =
    !Array.isArray(product.options) ||
    product.options.some(
      (option) =>
        !option ||
        ["name", "price", "stock"].some((key) => Object.prototype.hasOwnProperty.call(option, key)),
    );

  const hasLegacyCollectionKeys =
    !Array.isArray(product.collections) ||
    product.collections.some(
      (collection) => !collection || Object.prototype.hasOwnProperty.call(collection, "name"),
    );

  return (
    !hasLegacyProductKeys &&
    !hasLegacyOptionKeys &&
    !hasLegacyCollectionKeys &&
    typeof product.title === "string" &&
    Object.prototype.hasOwnProperty.call(product, "salePrice") &&
    Array.isArray(product.photos) &&
    Array.isArray(product.options) &&
    Array.isArray(product.collections)
  );
};

const useCart = create(
  persist(
    (set, get) => ({
      products: [],
      discountedAmount: undefined,
      couponId: undefined,
      couponCode: undefined,
      hasHydrated: false,

      setHasHydrated: (hasHydrated) => set({ hasHydrated }),

      /** Full cart object for existing UI (`{ products, discountedAmount?, ... }`). */
      getCart: () => {
        const s = get();
        const cart = { products: s.products };
        if (s.discountedAmount != null) {
          cart.discountedAmount = s.discountedAmount;
          cart.couponId = s.couponId;
          cart.couponCode = s.couponCode;
        }
        return cart;
      },

      setCart: (cartData) => {
        if (!cartData) return;
        set({
          products: Array.isArray(cartData.products) ? [...cartData.products] : [],
          discountedAmount: cartData.discountedAmount,
          couponId: cartData.couponId,
          couponCode: cartData.couponCode,
        });
      },

      applyPromoCode: (discountedAmount, couponId, couponCode) =>
        set({ discountedAmount, couponId, couponCode }),

      removePromoCode: () =>
        set({
          discountedAmount: undefined,
          couponId: undefined,
          couponCode: undefined,
        }),

      clearCart: () =>
        set({
          products: [],
          discountedAmount: undefined,
          couponId: undefined,
          couponCode: undefined,
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ products: state.products }),
    },
  ),
);

function markHydrated() {
  const state = useCart.getState();

  if (
    !Array.isArray(state.products) ||
    state.products.some((product) => !isCurrentCartProduct(product))
  ) {
    state.clearCart();
    useCart.persist.clearStorage();
  }

  useCart.setState({ hasHydrated: true });
}

if (typeof window !== "undefined") {
  useCart.persist.onFinishHydration(markHydrated);
  // Rehydration may finish before this subscriber is attached.
  Promise.resolve().then(() => {
    if (useCart.persist.hasHydrated()) markHydrated();
  });
}

export default useCart;
